import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorExpired - The expired coupon error element.
 * @property {HTMLElement} cartDiscountErrorAlreadyUsed - The already used coupon error element.
 * @property {HTMLElement} cartDiscountErrorSetProducts - The set products error element.
 * @property {HTMLElement} cartDiscountSuccess - The success message element.
 * @property {HTMLElement} cartDiscountSuccessText - The success message text element.
 * @property {HTMLElement} cartDiscountWarning - The warning message element.
 * @property {HTMLElement} cartDiscountWarningText - The warning message text element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /**
   * Custom coupon codes data - will be populated from metaobjects
   * For now, we'll fetch from a global variable or use localStorage
   */
  customCoupons = [];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Initialize custom coupon system with cart data
   */
  #initCustomCouponSystem() {
    // Get cart items from data attribute
    let cartItems = [];
    try {
      const cartItemsData = this.dataset.cartItems;
      if (cartItemsData) {
        cartItems = JSON.parse(cartItemsData);
      }
    } catch (e) {
      console.error('Error parsing cart items:', e);
    }

    // Get custom coupons from global variable (set by Liquid)
    const customCoupons = window.OsletCustomCouponsData || [];

    // Initialize the custom coupon system
    if (window.OsletCustomCoupon) {
      window.OsletCustomCoupon.init(customCoupons, cartItems);
    }
  }

  /**
   * Hide all custom coupon messages
   */
  #hideAllCustomMessages() {
    const refs = this.refs;

    // Hide all error messages
    if (refs.cartDiscountError) refs.cartDiscountError.classList.add('hidden');
    if (refs.cartDiscountErrorDiscountCode) refs.cartDiscountErrorDiscountCode.classList.add('hidden');
    if (refs.cartDiscountErrorShipping) refs.cartDiscountErrorShipping.classList.add('hidden');
    if (refs.cartDiscountErrorExpired) refs.cartDiscountErrorExpired.classList.add('hidden');
    if (refs.cartDiscountErrorAlreadyUsed) refs.cartDiscountErrorAlreadyUsed.classList.add('hidden');
    if (refs.cartDiscountErrorSetProducts) refs.cartDiscountErrorSetProducts.classList.add('hidden');

    // Hide success and warning
    if (refs.cartDiscountSuccess) refs.cartDiscountSuccess.classList.add('hidden');
    if (refs.cartDiscountWarning) refs.cartDiscountWarning.classList.add('hidden');
  }

  /**
   * Show custom coupon error
   * @param {string} errorType - Type of error to show
   */
  #showCustomCouponError(errorType) {
    const refs = this.refs;

    if (refs.cartDiscountError) {
      refs.cartDiscountError.classList.remove('hidden');
    }

    switch (errorType) {
      case 'expired':
        if (refs.cartDiscountErrorExpired) {
          refs.cartDiscountErrorExpired.classList.remove('hidden');
        }
        break;
      case 'already_used':
        if (refs.cartDiscountErrorAlreadyUsed) {
          refs.cartDiscountErrorAlreadyUsed.classList.remove('hidden');
        }
        break;
      case 'all_ineligible':
      case 'no_eligible':
        if (refs.cartDiscountErrorSetProducts) {
          refs.cartDiscountErrorSetProducts.classList.remove('hidden');
        }
        break;
      default:
        if (refs.cartDiscountErrorDiscountCode) {
          refs.cartDiscountErrorDiscountCode.classList.remove('hidden');
        }
    }
  }

  /**
   * Show custom coupon success message
   * @param {string} message - Success message to display
   * @param {boolean} hasWarning - Whether there's also a warning about ineligible items
   * @param {string} warningMessage - Warning message about set products
   */
  #showCustomCouponSuccess(message, hasWarning = false, warningMessage = '') {
    const refs = this.refs;

    if (refs.cartDiscountSuccess && refs.cartDiscountSuccessText) {
      refs.cartDiscountSuccessText.textContent = message;
      refs.cartDiscountSuccess.classList.remove('hidden');
    }

    if (hasWarning && refs.cartDiscountWarning && refs.cartDiscountWarningText) {
      refs.cartDiscountWarningText.textContent = warningMessage;
      refs.cartDiscountWarning.classList.remove('hidden');
    }
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();

    // Hide all previous messages
    this.#hideAllCustomMessages();

    // Initialize custom coupon system
    this.#initCustomCouponSystem();

    // Check if this is a custom coupon first
    if (window.OsletCustomCoupon) {
      const customResult = window.OsletCustomCoupon.applyCoupon(discountCodeValue);

      // If it's a custom coupon, handle it
      if (customResult.isCustomCoupon) {
        if (!customResult.success) {
          // Show appropriate error
          this.#showCustomCouponError(customResult.errorType);
          discountCode.value = '';
          return;
        }

        // Custom coupon validated successfully
        // Now also apply via Shopify (the matching discount code)
        // But first show our success message
        if (customResult.hasIneligibleItems) {
          const warningMsg = `Note: ${customResult.ineligibleCount} set product(s) are not eligible for this coupon.`;
          this.#showCustomCouponSuccess(
            `Coupon applied! You save ${window.OsletCustomCoupon.formatPrice(customResult.calculation.discountAmount)} on ${customResult.calculation.analysis.eligibleCount} product(s).`,
            true,
            warningMsg
          );
        } else {
          this.#showCustomCouponSuccess(
            `Coupon applied successfully! You save ${window.OsletCustomCoupon.formatPrice(customResult.calculation.discountAmount)}`
          );
        }

        // Dispatch custom event for cart summary to update
        document.dispatchEvent(new CustomEvent('oslet:custom-coupon-applied', {
          detail: {
            code: discountCodeValue,
            discountAmount: customResult.calculation.discountAmount,
            eligibleCount: customResult.calculation.analysis.eligibleCount,
            ineligibleCount: customResult.calculation.analysis.ineligibleCount,
            discountPercent: customResult.coupon.discount_percent
          }
        }));

        // Continue to apply via Shopify for actual checkout discount
        // Fall through to Shopify logic below
      }
      // If not a custom coupon (letShopifyHandle), continue with normal flow
    }

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
